/**
 * `folderWire.ts` — the `update_folder` session-event contract (tc **57**).
 *
 * Unity reference: `Models/EventModel/EventData/FolderEventData.cs`, the four
 * senders in `AWSServerEventSenderModel.cs:257-308`, and the dispatch in
 * `MultiuserAppModel.cs:1354-1367`.
 *
 * **All four folder operations share ONE wire type**, `update_folder`, and are
 * told apart by the `folderEvent` discriminator — there is no `create_folder`
 * or `delete_folder` string. tc 57 = `FolderEventData`'s index in
 * `SessionModel.m_typeNames` (`SessionModel.cs:388-447`); a wrong-but-valid tc
 * makes a Unity peer deserialize the frame as a bare `BaseEventData` and lose
 * the whole payload silently.
 *
 * **The field names here are the JsonUtility ones — the lowercase private
 * `[SerializeField]` backing fields, not the C# properties.** So `creatorID`
 * (capital ID), `projectIDs`, `isShared`, and epoch-MILLISECOND longs for
 * `createdTS` / `modifiedTS` — which is a different representation from the
 * same two values in Dynamo, where `folderDto.ts` writes them as ISO strings.
 * Getting this wrong is invisible on a web-only test (both ends would agree)
 * and breaks only against a desktop peer.
 *
 * **Three of the four events carry a PARTIAL folder**, deliberately: rename
 * sends `{ID, Name}`, the membership change sends `{ID, ProjectIDs}`, and
 * delete sends `{ID}` alone. So a receiver must merge the named fields onto
 * what it already holds and must never treat an absent field as a clear — that
 * is what `mergeWireFolder` below is for.
 *
 * Folder events carry NO `projectId` (the senders take a `customerId` and then
 * never write it to the payload — `AWSServerEventSenderModel.cs:257`), so they
 * belong with the project-LIST family in the reconciler, ahead of the
 * active-project scoping guard.
 */
import type { FolderInfo } from "./folderDto";

/** Port of `FolderEventType` (`FolderEventData.cs:6-12`) — ordinal values. */
export const FOLDER_EVENT_TYPE = {
  Create: 0,
  Delete: 1,
  UpdateName: 2,
  UpdateProjectList: 3,
} as const;

export type FolderEventType =
  (typeof FOLDER_EVENT_TYPE)[keyof typeof FOLDER_EVENT_TYPE];

/** JsonUtility shape of `FolderData`. */
export interface WireFolder {
  name?: string;
  id?: string;
  creatorID?: string;
  createdTS?: number;
  modifiedTS?: number;
  author?: string;
  isShared?: boolean;
  projectIDs?: string[];
}

/**
 * Full folder for the Create event. The other three legs build their partials
 * at the call site, matching Unity's own senders.
 */
export function folderToWire(folder: FolderInfo): WireFolder {
  return {
    name: folder.name,
    id: folder.id,
    creatorID: folder.creatorId,
    createdTS: toUnixMs(folder.createdAt),
    modifiedTS: toUnixMs(folder.modifiedAt),
    author: folder.author,
    isShared: folder.isSharedFolder,
    projectIDs: [...folder.projectIds],
  };
}

export function isWireFolder(value: unknown): value is WireFolder {
  return typeof value === "object" && value !== null;
}

/** The folder id off any of the four payload shapes. */
export function wireFolderId(wire: WireFolder): string {
  return typeof wire.id === "string" ? wire.id : "";
}

/**
 * Build a complete `FolderInfo` from a Create payload. Only used for Create,
 * where Unity sends the whole record; the partial legs go through
 * {@link mergeWireFolder} instead.
 */
export function folderFromWire(wire: WireFolder, customerId: string): FolderInfo {
  return {
    id: wireFolderId(wire),
    name: typeof wire.name === "string" ? wire.name : "",
    creatorId: typeof wire.creatorID === "string" ? wire.creatorID : "",
    author: typeof wire.author === "string" ? wire.author : "",
    customerId,
    isSharedFolder: wire.isShared === true,
    createdAt: fromUnixMs(wire.createdTS) ?? new Date().toISOString(),
    modifiedAt:
      fromUnixMs(wire.modifiedTS) ??
      fromUnixMs(wire.createdTS) ??
      new Date().toISOString(),
    projectIds: Array.isArray(wire.projectIDs) ? [...wire.projectIDs] : [],
  };
}

/**
 * Apply a **rename** payload onto a folder we already hold.
 *
 * **There is deliberately no general "merge a wire folder" helper**, and that is
 * the single most important thing in this file. A rename's payload is
 * `new FolderData { ID, Name }` — but `FolderData`'s parameterless constructor
 * runs first and sets `ProjectIDs = new List<string>()` (`FolderData.cs:76-81`),
 * and JsonUtility serializes a non-null empty list, so **every Unity rename
 * arrives carrying `projectIDs: []`**. It also carries `creatorID: ""`,
 * `author: ""`, `isShared: false`, and a `createdTS`/`modifiedTS` pair stamped
 * at send time. None of those are true; they are ctor defaults for fields the
 * operation does not own.
 *
 * So a helper that merges "whatever is present" empties the folder on every
 * rename, and re-assigns its ownership and shared flag while it is at it. That
 * bug is invisible in a web-only two-tab test, because the web's own rename
 * sender omits those fields entirely — it only shows up against a desktop peer.
 *
 * Hence: one function per operation, each applying ONLY what its operation
 * owns. If a fifth folder operation is ever added, give it its own function
 * here rather than generalizing these.
 */
export function applyWireFolderRename(
  current: FolderInfo,
  wire: WireFolder,
): FolderInfo {
  const name = typeof wire.name === "string" ? wire.name : "";
  if (!name || name === current.name) return current;
  return { ...current, name };
}

/**
 * Unity's `OnBeforeSerialize` uses `TimeUtils.ToUnixTimeMilliseconds`. A stamp
 * we cannot parse becomes 0 rather than NaN — `JsonUtility` would read NaN into
 * a `long` field as garbage.
 */
function toUnixMs(iso: string | undefined): number {
  if (!iso) return 0;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * `undefined` for anything that is not a usable stamp — including 0 and the
 * large negative value a default-constructed `DateTime` serializes to, both of
 * which reach us on the partial legs where Unity never set the property.
 * Treating those as real dates would drag a folder to the bottom of a Modified
 * sort every time a peer renamed it.
 */
function fromUnixMs(value: number | undefined): string | undefined {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }
  return new Date(value).toISOString();
}
